
import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { useMutation, useQuery } from "convex/react";
import { api } from "./convex/_generated/api";

import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";

import "./SocialRelationshipsPage.css";

export default function SocialRelationshipsPage() {
  const { progressId, userId } = useParams();

  /* =================================
     DAILY INPUT STATE
  ================================= */

  const [
    socialConnectionMinutes,
    setSocialConnectionMinutes,
  ] = useState("");

  const [
    familyTimeMinutes,
    setFamilyTimeMinutes,
  ] = useState("");

  const [
    friendTimeMinutes,
    setFriendTimeMinutes,
  ] = useState("");

  const [
    communityMinutes,
    setCommunityMinutes,
  ] = useState("");

  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  /* =================================
     CONVEX MUTATION
  ================================= */

  const calculateSocialRelationships =
    useMutation(
      api.progress.calculateSocialRelationships
    );

  /* =================================
     GET SAVED RESULTS
  ================================= */

  const usualresult = useQuery(
    api.progress.getSocialRelationshipsResults,
    progressId && userId
      ? {
          progressId,
          userId,
        }
      : "skip"
  );

  console.log(
    "SOCIAL RELATIONSHIPS USUAL RESULT:",
    usualresult
  );

  /* =================================
     DAILY + WEEKLY RECORDS
  ================================= */

  const dailyRecords =
    usualresult?.dailyRecords ?? [];

  console.log("daily");

  const weeklyRecords =
    usualresult?.weeklyRecords ?? [];

  const sortedDailyRecords =
    [...dailyRecords].sort(
      (a, b) =>
        a.date.localeCompare(b.date)
    );

  const sortedWeeklyRecords =
    [...weeklyRecords].sort(
      (a, b) =>
        a.weekStart.localeCompare(
          b.weekStart
        )
    );

  console.log(sortedWeeklyRecords);

  const latestWeeklyRecord =
    sortedWeeklyRecords.length > 0
      ? sortedWeeklyRecords[
          sortedWeeklyRecords.length - 1
        ]
      : null;

  /* =================================
     SAFE NUMBER
  ================================= */

  const safeNumber = (value) => {
    const number = Number(value);

    return Number.isFinite(number)
      ? number
      : 0;
  };

  /* =================================
     DAILY SCORE CHART
  ================================= */

  const dailyScoreChartData =
    sortedDailyRecords.map((record) => ({
      date: record.date,

      dailyScore: Math.round(
        Math.max(
          0,
          Math.min(
            100,
            safeNumber(record.dailyScore)
          )
        )
      ),
    }));

  console.log(
    "SOCIAL DAILY RECORDS:",
    dailyRecords
  );

  console.log(
    "SOCIAL DAILY CHART DATA:",
    dailyScoreChartData
  );

  /* =================================
     WEEKLY SCORE CHART
  ================================= */

  const weeklyScoreChartData =
    sortedWeeklyRecords.map((record) => ({
      week: record.weekStart,

      trackingScore: Math.round(
        Math.max(
          0,
          Math.min(
            100,
            safeNumber(
              record.trackingScore
            )
          )
        )
      ),

      activityScore: Math.round(
        Math.max(
          0,
          Math.min(
            100,
            safeNumber(
              record.activityScore
            )
          )
        )
      ),

      finalScore: Math.round(
        Math.max(
          0,
          Math.min(
            100,
            safeNumber(
              record.finalScore
            )
          )
        )
      ),
    }));

  console.log(
    "SOCIAL WEEKLY RECORDS:",
    weeklyRecords
  );

  console.log(
    "SOCIAL WEEKLY CHART DATA:",
    weeklyScoreChartData
  );

  /* =================================
     LATEST WEEK ACTIVITY CHART
  ================================= */

  const socialMinutesData =
    latestWeeklyRecord
      ? [
          {
            name: "Social",
            minutes: safeNumber(
              latestWeeklyRecord
                .averageSocialConnectionMinutes
            ),
          },

          {
            name: "Family",
            minutes: safeNumber(
              latestWeeklyRecord
                .averageFamilyTimeMinutes
            ),
          },

          {
            name: "Friends",
            minutes: safeNumber(
              latestWeeklyRecord
                .averageFriendTimeMinutes
            ),
          },

          {
            name: "Community",
            minutes: safeNumber(
              latestWeeklyRecord
                .averageCommunityMinutes
            ),
          },
        ]
      : [];

  /* =================================
     GET TODAY
  ================================= */

  const getToday = () => {
    const today = new Date();

    return today
      .toISOString()
      .split("T")[0];
  };

  /* =================================
     GET CURRENT WEEK
     MONDAY → SUNDAY
  ================================= */

  const getCurrentWeek = () => {
    const today = new Date();

    const day = today.getDay();

    const daysSinceMonday =
      day === 0
        ? 6
        : day - 1;

    const monday = new Date(today);

    monday.setDate(
      today.getDate() -
        daysSinceMonday
    );

    const sunday = new Date(monday);

    sunday.setDate(
      monday.getDate() + 6
    );

    return {
      weekStart:
        monday
          .toISOString()
          .split("T")[0],

      weekEnd:
        sunday
          .toISOString()
          .split("T")[0],
    };
  };

  /* =================================
     SUBMIT
  ================================= */

  const handleSubmit = async (e) => {
    e.preventDefault();

    setError("");
    setResult(null);

    if (!progressId || !userId) {
      setError(
        "Missing progress or user information."
      );

      return;
    }

    if (
      socialConnectionMinutes === "" ||
      familyTimeMinutes === "" ||
      friendTimeMinutes === "" ||
      communityMinutes === ""
    ) {
      setError(
        "Please enter all Social & Relationships information."
      );

      return;
    }

    const social =
      Number(socialConnectionMinutes);

    const family =
      Number(familyTimeMinutes);

    const friends =
      Number(friendTimeMinutes);

    const community =
      Number(communityMinutes);

    if (
      !Number.isFinite(social) ||
      !Number.isFinite(family) ||
      !Number.isFinite(friends) ||
      !Number.isFinite(community)
    ) {
      setError(
        "Please enter valid numbers."
      );

      return;
    }

    if (
      social < 0 ||
      family < 0 ||
      friends < 0 ||
      community < 0
    ) {
      setError(
        "Minutes cannot be negative."
      );

      return;
    }

    try {
      setLoading(true);

      const date = getToday();

      const {
        weekStart,
        weekEnd,
      } = getCurrentWeek();

      const response =
        await calculateSocialRelationships({
          userId,
          progressId,

          date,

          socialConnectionMinutes:
            social,

          familyTimeMinutes:
            family,

          friendTimeMinutes:
            friends,

          communityMinutes:
            community,

          weekStart,
          weekEnd,
        });

      console.log(
        "Social & Relationships Result:",
        response
      );

      setResult({
        ...response,
        date,
      });

    } catch (err) {
      console.error(
        "Social & Relationships Error:",
        err
      );

      setError(
        err?.message ||
          "Failed to save Social & Relationships data."
      );
    } finally {
      setLoading(false);
    }
  };

  /* =================================
     CURRENT SCORE CHART
  ================================= */

  const scoreChartData = result
    ? [
        {
          name: "Tracking",
          score: Math.round(
            Math.max(
              0,
              Math.min(
                100,
                safeNumber(
                  result.trackingScore
                )
              )
            )
          ),
        },

        {
          name: "Activities",
          score: Math.round(
            Math.max(
              0,
              Math.min(
                100,
                safeNumber(
                  result.activityScore
                )
              )
            )
          ),
        },

        {
          name: "Final",
          score: Math.round(
            Math.max(
              0,
              Math.min(
                100,
                safeNumber(
                  result.finalScore
                )
              )
            )
          ),
        },
      ]
    : [];

  return (
    <div className="social-page">

      {/* =================================
          HEADER
      ================================= */}

      <div className="social-container">

        <header className="social-header">
          <span className="social-eyebrow">
            SOCIAL WELLNESS
          </span>

          <h1>
            Social & Relationships
          </h1>

          <p>
            Track the time you invest in
            relationships and see how it
            contributes to your weekly score.
          </p>
        </header>

        {/* =================================
            DAILY FORM
        ================================= */}

        <form
          onSubmit={handleSubmit}
          className="social-form"
        >

          <div className="form-heading">
            <div className="form-heading-icon">
              ♡
            </div>

            <div>
              <h2>
                Daily Relationship Check-in
              </h2>

              <p>
                Record how much time you spent
                connecting with others today.
              </p>
            </div>
          </div>

          {/* SOCIAL */}

          <div className="input-group">
            <label>
              Social connection minutes today
            </label>

            <input
              type="number"
              min="0"
              value={socialConnectionMinutes}
              onChange={(e) =>
                setSocialConnectionMinutes(
                  e.target.value
                )
              }
              placeholder="Example: 30"
            />
          </div>

          {/* FAMILY */}

          <div className="input-group">
            <label>
              Family time minutes today
            </label>

            <input
              type="number"
              min="0"
              value={familyTimeMinutes}
              onChange={(e) =>
                setFamilyTimeMinutes(
                  e.target.value
                )
              }
              placeholder="Example: 60"
            />
          </div>

          {/* FRIENDS */}

          <div className="input-group">
            <label>
              Friend time minutes today
            </label>

            <input
              type="number"
              min="0"
              value={friendTimeMinutes}
              onChange={(e) =>
                setFriendTimeMinutes(
                  e.target.value
                )
              }
              placeholder="Example: 45"
            />
          </div>

          {/* COMMUNITY */}

          <div className="input-group">
            <label>
              Community time minutes today
            </label>

            <input
              type="number"
              min="0"
              value={communityMinutes}
              onChange={(e) =>
                setCommunityMinutes(
                  e.target.value
                )
              }
              placeholder="Example: 20"
            />
          </div>

          {/* SUBMIT */}

          <button
            type="submit"
            disabled={loading}
            className="social-submit"
          >
            {loading
              ? "Saving..."
              : "Save Social & Relationships"}
          </button>

        </form>

        {/* =================================
            ERROR
        ================================= */}

        {error && (
          <div className="social-error">
            <span>!</span>
            {error}
          </div>
        )}

        {/* =================================
            CURRENT RESULT
        ================================= */}

        {result && (
          <div className="results-section">

            {/* DAILY SCORE */}

            <div className="daily-score-card">
              <div>
                <span className="card-label">
                  TODAY'S SCORE
                </span>

                <p>
                  Today's Score
                </p>
              </div>

              <div className="daily-score-value">
                {Math.round(
                  safeNumber(
                    result.dailyScore
                  )
                )}
              </div>
            </div>

            {/* WEEKLY SCORE CARDS */}

            <div>
              <div className="section-title">
                <span>WEEKLY PERFORMANCE</span>
                <h2>
                  Weekly Score
                </h2>
              </div>

              <div className="score-grid">

                <div className="score-card green-card">
                  <span>
                    Tracking Score
                  </span>

                  <strong>
                    {Math.round(
                      safeNumber(
                        result.trackingScore
                      )
                    )}
                  </strong>
                </div>

                <div className="score-card orange-card">
                  <span>
                    Activity Score
                  </span>

                  <strong>
                    {Math.round(
                      safeNumber(
                        result.activityScore
                      )
                    )}
                  </strong>
                </div>

                <div className="score-card final-card">
                  <span>
                    Final Score
                  </span>

                  <strong>
                    {Math.round(
                      safeNumber(
                        result.finalScore
                      )
                    )}
                  </strong>
                </div>

              </div>
            </div>

            {/* CURRENT SCORE CHART */}

            {scoreChartData.length > 0 && (
              <div className="chart-card">

                <div className="chart-header">
                  <div>
                    <span className="chart-kicker">
                      PERFORMANCE
                    </span>

                    <h2>
                      Score Comparison
                    </h2>
                  </div>

                  <span className="score-range">
                    0 — 100
                  </span>
                </div>

                <div className="chart-container">
                  <ResponsiveContainer
                    width="100%"
                    height="100%"
                  >
                    <BarChart
                      data={scoreChartData}
                    >
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke="#26382f"
                      />

                      <XAxis
                        dataKey="name"
                        tick={{
                          fill: "#9ca3af",
                          fontSize: 12,
                        }}
                        axisLine={{
                          stroke: "#33443a",
                        }}
                        tickLine={false}
                      />

                      <YAxis
                        domain={[0, 100]}
                        tick={{
                          fill: "#9ca3af",
                          fontSize: 12,
                        }}
                        axisLine={false}
                        tickLine={false}
                      />

                      <Tooltip
                        contentStyle={{
                          background:
                            "#111a16",
                          border:
                            "1px solid #33483c",
                          borderRadius:
                            "12px",
                          color: "#ffffff",
                        }}
                        labelStyle={{
                          color: "#ffffff",
                        }}
                      />

                      <Legend
                        wrapperStyle={{
                          color: "#9ca3af",
                        }}
                      />

                      <Bar
                        dataKey="score"
                        name="Score"
                        fill="#22c55e"
                        radius={[
                          8,
                          8,
                          0,
                          0,
                        ]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

              </div>
            )}

          </div>
        )}

        {/* =================================
            DAILY SCORE HISTORY
        ================================= */}

        {dailyScoreChartData.length > 0 && (
          <div className="chart-card history-card">

            <div className="chart-header">
              <div>
                <span className="chart-kicker">
                  DAILY HISTORY
                </span>

                <h2>
                  Daily Social & Relationships Scores
                </h2>
              </div>
            </div>

            <div className="chart-container">
              <ResponsiveContainer
                width="100%"
                height="100%"
              >
                <BarChart
                  data={dailyScoreChartData}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="#26382f"
                  />

                  <XAxis
                    dataKey="date"
                    tick={{
                      fill: "#9ca3af",
                      fontSize: 12,
                    }}
                    axisLine={{
                      stroke: "#33443a",
                    }}
                    tickLine={false}
                  />

                  <YAxis
                    domain={[0, 100]}
                    tick={{
                      fill: "#9ca3af",
                      fontSize: 12,
                    }}
                    axisLine={false}
                    tickLine={false}
                  />

                  <Tooltip
                    contentStyle={{
                      background:
                        "#111a16",
                      border:
                        "1px solid #33483c",
                      borderRadius:
                        "12px",
                      color: "#ffffff",
                    }}
                  />

                  <Legend />

                  <Bar
                    dataKey="dailyScore"
                    name="Daily Score"
                    fill="#22c55e"
                    radius={[
                      8,
                      8,
                      0,
                      0,
                    ]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>

          </div>
        )}

        {/* =================================
            WEEKLY SCORE HISTORY
        ================================= */}

        {weeklyScoreChartData.length > 0 && (
          <div className="chart-card history-card">

            <div className="chart-header">
              <div>
                <span className="chart-kicker">
                  WEEKLY HISTORY
                </span>

                <h2>
                  Weekly Social & Relationships Scores
                </h2>
              </div>
            </div>

            <div className="chart-container">
              <ResponsiveContainer
                width="100%"
                height="100%"
              >
                <BarChart
                  data={weeklyScoreChartData}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="#26382f"
                  />

                  <XAxis
                    dataKey="week"
                    tick={{
                      fill: "#9ca3af",
                      fontSize: 12,
                    }}
                    axisLine={{
                      stroke: "#33443a",
                    }}
                    tickLine={false}
                  />

                  <YAxis
                    domain={[0, 100]}
                    tick={{
                      fill: "#9ca3af",
                      fontSize: 12,
                    }}
                    axisLine={false}
                    tickLine={false}
                  />

                  <Tooltip
                    contentStyle={{
                      background:
                        "#111a16",
                      border:
                        "1px solid #33483c",
                      borderRadius:
                        "12px",
                      color: "#ffffff",
                    }}
                  />

                  <Legend />

                  <Bar
                    dataKey="trackingScore"
                    name="Tracking Score"
                    fill="#22c55e"
                    radius={[
                      6,
                      6,
                      0,
                      0,
                    ]}
                  />

                  <Bar
                    dataKey="activityScore"
                    name="Activity Score"
                    fill="#f97316"
                    radius={[
                      6,
                      6,
                      0,
                      0,
                    ]}
                  />

                  <Bar
                    dataKey="finalScore"
                    name="Final Score"
                    fill="#eab308"
                    radius={[
                      6,
                      6,
                      0,
                      0,
                    ]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>

          </div>
        )}

        {/* =================================
            RELATIONSHIP TIME
        ================================= */}

        {socialMinutesData.length > 0 && (
          <div className="chart-card history-card">

            <div className="chart-header">
              <div>
                <span className="chart-kicker">
                  RELATIONSHIP BALANCE
                </span>

                <h2>
                  Average Relationship Time
                </h2>
              </div>
            </div>

            <div className="chart-container">
              <ResponsiveContainer
                width="100%"
                height="100%"
              >
                <BarChart
                  data={socialMinutesData}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="#26382f"
                  />

                  <XAxis
                    dataKey="name"
                    tick={{
                      fill: "#9ca3af",
                      fontSize: 12,
                    }}
                    axisLine={{
                      stroke: "#33443a",
                    }}
                    tickLine={false}
                  />

                  <YAxis
                    tick={{
                      fill: "#9ca3af",
                      fontSize: 12,
                    }}
                    axisLine={false}
                    tickLine={false}
                  />

                  <Tooltip
                    contentStyle={{
                      background:
                        "#111a16",
                      border:
                        "1px solid #33483c",
                      borderRadius:
                        "12px",
                      color: "#ffffff",
                    }}
                  />

                  <Legend />

                  <Bar
                    dataKey="minutes"
                    name="Minutes per Day"
                    fill="#f97316"
                    radius={[
                      8,
                      8,
                      0,
                      0,
                    ]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>

          </div>
        )}

        {/* =================================
            WEEKLY DETAILS
        ================================= */}

        {latestWeeklyRecord && (
          <div className="weekly-details">

            <div className="details-header">
              <span className="chart-kicker">
                THIS WEEK
              </span>

              <h2>
                Weekly Details
              </h2>
            </div>

            <div className="details-grid">

              <p>
                Days measured:{" "}
                <strong>
                  {safeNumber(
                    latestWeeklyRecord.daysMeasured
                  )}
                </strong>
              </p>

              <p>
                Social & Relationships
                suggestions:{" "}
                <strong>
                  {safeNumber(
                    latestWeeklyRecord.suggestionCount
                  )}
                </strong>
              </p>

              <p>
                Average social connection:{" "}
                <strong>
                  {safeNumber(
                    latestWeeklyRecord
                      .averageSocialConnectionMinutes
                  ).toFixed(1)}{" "}
                  min/day
                </strong>
              </p>

              <p>
                Average family time:{" "}
                <strong>
                  {safeNumber(
                    latestWeeklyRecord
                      .averageFamilyTimeMinutes
                  ).toFixed(1)}{" "}
                  min/day
                </strong>
              </p>

              <p>
                Average friend time:{" "}
                <strong>
                  {safeNumber(
                    latestWeeklyRecord
                      .averageFriendTimeMinutes
                  ).toFixed(1)}{" "}
                  min/day
                </strong>
              </p>

              <p>
                Average community time:{" "}
                <strong>
                  {safeNumber(
                    latestWeeklyRecord
                      .averageCommunityMinutes
                  ).toFixed(1)}{" "}
                  min/day
                </strong>
              </p>

            </div>

          </div>
        )}

      </div>
    </div>
  );
}
